import languageLocaleUtils from 'utils/LanguageLocale';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import UserUtils from 'utils/User';
import Empty from 'constants/empty';
import getAddressConst from 'constants/Address';

const US_ZIP_CODE_FORMAT = /\d{5}(?:[-\s]\d{4})?$/;

const { COUNTRIES } = languageLocaleUtils;

// Do not use this method on billing addresses since AVS shoutd not be enabled on billing addresses
function hasAVS(country) {
    return Sephora.configurationSettings.enableAddressValidation && (country === COUNTRIES.CA || country === COUNTRIES.US);
}

function formatZipPostalCode(inputData, divider, leftLength, totalLength) {
    let formatZipPostal = inputData;

    if (formatZipPostal) {
        if (formatZipPostal.length > leftLength && formatZipPostal.indexOf(divider) === -1) {
            // adds a hyphen/space if zip/postal code is missing one and needs it
            // calls autoFill function if zip/postal code after hyphen/space is 10/6 characters long
            formatZipPostal = `${formatZipPostal.slice(0, leftLength)}${divider}` + `${formatZipPostal.slice(leftLength, totalLength)}`;
        } else if (formatZipPostal.length === leftLength + 1) {
            // remove hyphen/space in zip/postal code if user removes the chars to right of divider
            if (formatZipPostal.slice(0, leftLength).indexOf(divider) !== -1) {
                formatZipPostal = formatZipPostal.replace(divider, '');
            }

            formatZipPostal = formatZipPostal.slice(0, leftLength);
        } else if (formatZipPostal.slice(0, leftLength).indexOf(divider) !== -1) {
            //remove hyphen/space if user deletes numbers in zip code well before hyphen/space
            formatZipPostal = formatZipPostal.replace(divider, '');

            if (formatZipPostal.length > leftLength) {
                formatZipPostal = `${formatZipPostal.slice(0, leftLength)}${divider}` + `${formatZipPostal.slice(leftLength, totalLength)}`;
            }
        }
    }

    return formatZipPostal;
}

// Removes the extra 4 digits of a zipcode
function formatZipCode(zip) {
    return (zip || '').split('-')[0];
}

/**
 * Compare two zip codes (handles ZIP+4 format)
 * @param {string} zip1 - First zip code
 * @param {string} zip2 - Second zip code
 * @returns {boolean} - True if base zip codes match
 * @example zipCodesMatch('14614', '14614-2020') returns true
 */
function zipCodesMatch(zip1, zip2) {
    if (!zip1 || !zip2) {
        return false;
    }

    // Extract base ZIP code (part before hyphen) for comparison
    // This handles ZIP+4 format where "14614" should match "14614-2020"
    const baseZip1 = formatZipCode(zip1);
    const baseZip2 = formatZipCode(zip2);

    return baseZip1 === baseZip2;
}

function updateLocalDefaultShippingAddressData(defaultSAData) {
    const { defaultSACountryCode, defaultSAZipCode } = defaultSAData;
    const userData = Storage.local.getItem(LOCAL_STORAGE.USER_DATA) || Empty.Object;

    if (userData?.profile) {
        Object.assign(userData.profile, {
            defaultSACountryCode,
            defaultSAZipCode
        });

        Storage.local.setItem(LOCAL_STORAGE.USER_DATA, userData, UserUtils.USER_DATA_EXPIRY);
    }
}

const defaultsByCountry = {
    US: { preferredZipCode: '20147' },
    CA: { preferredZipCode: 'M5B 2H1' }
};

function getPreferredZipCodeOrDefault(preferredZipCode, options = Empty.Object) {
    const country = options.country || 'US';

    return preferredZipCode || defaultsByCountry[country].preferredZipCode;
}

// GIM-5452 (E2-3): HIGHLIGHT_FIELD_MAP is locale-independent (a pure server-field -> input-key
// mapping with no copy), so cache it on first use instead of rebuilding the full localized
// address-constants object (getAddressConst, which includes modal copy) on every call.
let highlightFieldMapCache;

/**
 * Resolve the server-provided highlightedFields[] list into the Set of UFE form-input keys to mark.
 * The server-field -> input mapping is centralized in HIGHLIGHT_FIELD_MAP (constants/Address.js);
 * unknown field names are ignored. UFE applies no decision logic here — it only translates names the
 * service already chose. Gated behind avsLoqateV2: when the flag is off this returns an
 * empty Set, so the highlight feature is never applied regardless of any highlightedFields passed in.
 * @param {string[]} [highlightedFields] server field names from the AVS response
 * @returns {Set<string>} the UFE form-input keys to highlight (empty when the flag is off)
 */
function getHighlightedInputs(highlightedFields) {
    const inputs = new Set();

    // GIM-5452: feature-flag gate — highlight is an AVS Loqate v2 feature. With the flag off, nothing
    // is highlighted even if highlightedFields are somehow provided (bulletproof backward-compat).
    if (!Sephora.configurationSettings?.avsLoqateV2) {
        return inputs;
    }

    if (!Array.isArray(highlightedFields) || highlightedFields.length === 0) {
        return inputs;
    }

    if (!highlightFieldMapCache) {
        // Prototype-less copy so inherited keys (__proto__, constructor, toString, …) can never match
        // a serverField and yield a truthy non-string value — keeps the "unknown fields ignored" +
        // Set<string> contract even for adversarial/unexpected server field names.
        highlightFieldMapCache = Object.assign(Object.create(null), getAddressConst().HIGHLIGHT_FIELD_MAP);
    }

    highlightedFields.forEach(serverField => {
        const inputKey = highlightFieldMapCache[serverField];

        if (inputKey) {
            inputs.add(inputKey);
        }
    });

    return inputs;
}

export default {
    US_ZIP_CODE_FORMAT,
    hasAVS,
    formatZipPostalCode,
    formatZipCode,
    zipCodesMatch,
    updateLocalDefaultShippingAddressData,
    getPreferredZipCodeOrDefault,
    getHighlightedInputs
};
